import { z } from 'zod';
import type { UndoTree } from './undoTree';

/**
 * An undo tree read back from storage, its states parsed by `state`. Refuses
 * one whose nodes are not numbered in order or point outside the tree.
 */
export function parseUndoTree<S>(
  value: unknown,
  state: z.ZodType<S>,
): UndoTree<S> | null {
  const parsed = z
    .object({
      nodes: z
        .array(
          z.object({
            seq: z.number().int(),
            parent: z.number().int().nullable(),
            state,
            at: z.number(),
            label: z.string(),
            group: z.string().optional(),
          }),
        )
        .min(1),
      current: z.number().int(),
      redoTo: z.record(z.string(), z.number().int()),
    })
    .safeParse(value);
  if (!parsed.success) return null;
  const { nodes, current, redoTo } = parsed.data;
  const inTree = (seq: number) => seq >= 0 && seq < nodes.length;
  const ordered = nodes.every(
    (node, index) =>
      node.seq === index &&
      (index === 0
        ? node.parent === null
        : node.parent !== null && node.parent < index),
  );
  const pointsInside =
    inTree(current) && Object.values(redoTo).every((seq) => inTree(seq));
  return ordered && pointsInside ? { nodes, current, redoTo } : null;
}
